import React, { useMemo, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, parseApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Camera, Loader2, X } from "lucide-react";

export default function ProfileSetupDialog({ open, onOpenChange, user, onSaved }) {
  const { t } = useTranslation();
  const [alias, setAlias] = useState(user?.pseudonym || "");
  const [city, setCity] = useState(user?.city || "");
  const [interests, setInterests] = useState((user?.interests || []).join(", "));
  const [avatarUrl, setAvatarUrl] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const canSave = useMemo(() => alias.trim().length >= 2, [alias]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        setAvatarUrl(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
    } catch (e) {
      alert(parseApiError(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", {
        pseudonym: alias.trim(),
        city: city.trim() || null,
        interests: interests
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 10),
        picture_url: avatarUrl.trim() || null,
      });
      // Refresh auth state first, then close dialog
      if (onSaved) await onSaved();
      // Force close the dialog
      onOpenChange(false);
    } catch (e) {
      alert(parseApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-setup-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("profile_setup_title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Avatar Upload */}
          <div className="flex justify-center">
            <div className="relative">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="relative h-20 w-20 cursor-pointer overflow-hidden rounded-full border-2 border-slate-200 bg-slate-100 hover:border-blue-400 transition-colors"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-slate-400">
                    {alias ? alias[0].toUpperCase() : "?"}
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white shadow-md hover:bg-blue-700"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
              {avatarUrl && (
                <button
                  type="button"
                  onClick={() => setAvatarUrl("")}
                  className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  uploadAvatar(file);
                }}
              />
            </div>
          </div>
          <p className="text-center text-xs text-slate-500">Click to upload profile photo</p>

          <div>
            <div className="text-xs font-semibold text-slate-700">{t("alias_label")}</div>
            <Input
              data-testid="profile-alias-input"
              className="mt-1"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder={t("alias_placeholder")}
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700">City (optional)</div>
            <Input
              data-testid="profile-city-input"
              className="mt-1"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Jaipur"
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700">{t("interests_label")}</div>
            <Input
              data-testid="profile-interests-input"
              className="mt-1"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              placeholder="career, relationships, exams"
            />
          </div>

          <Button
            data-testid="profile-save-button"
            className="w-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white"
            disabled={!canSave || saving}
            onClick={save}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
